import type { DefectSuggestionProps } from "./DefectSuggestion.type";

import { BulbOutlined } from "@ant-design/icons";
import { Typography, Form, Flex, Spin } from "antd";
import { useEffect, useState } from "react";

import { SeverityFieldNames } from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/constants";
import {
  getDefectsSuggestions,
  getMaterialEndpoint,
} from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/DefectSuggestions";
import DecisionToolButton from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/DefectSuggestions/DecisionTool/DecisionToolButton";
import DefectsSuggestionItem from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/DefectSuggestions/DefectSuggestionItem";
import { SeverityGridPreview } from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/gridUtils";
import { useIsFormEnginePreviewMode } from "@/components/FormEngine/hooks/useIsFormEnginePreviewMode";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";

import { useDefectSuggestionsStableDatasetsImage } from "./DefectSuggestion.hook";
import { filterValidSuggestions } from "./DefectSuggestion.utils";

import "./DefectSuggestion.style.less";

const { Text } = Typography;
const { useFormInstance, useWatch } = Form;

const DefectSuggestion = ({
  value = "",
  onChange,
  onSelectedDefect,
  visibleDefects = [],
  datasetsImage = [],
  datasetsImageLoading,
}: DefectSuggestionProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<
    { defect: string; name?: string; confidence: number; suggested: boolean }[]
  >([]);
  const [visibleSuggestions, setVisibleSuggestions] = useState<
    { defect: string; suggested: boolean; confidence: number; name?: string }[]
  >([]);
  const isPreviewMode = useIsFormEnginePreviewMode();
  const { datasetsImageStable } =
    useDefectSuggestionsStableDatasetsImage(datasetsImage);
  const form = useFormInstance();
  const selectedMaterial = useWatch(SeverityFieldNames.material, form);
  const [errorMessage, setErrorMessage] = useState("");
  const isDefectSuggestionsActivated = useFeatureFlag("defectSuggestions");

  useEffect(() => {
    if (isPreviewMode) {
      const samples = SeverityGridPreview.buildSampleDefectsSuggestions();
      setSuggestions(samples.defects);
    } else if (getMaterialEndpoint(selectedMaterial)) {
      // If material is supported
      setIsLoading(true);
      getDefectsSuggestions({
        selectedMaterial,
        datasetsImage: datasetsImageStable,
      })
        .then(({ defects, error }) => {
          setSuggestions(defects);
          setErrorMessage(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isPreviewMode, selectedMaterial, datasetsImageStable]);

  useEffect(() => {
    const sortedSuggestions = filterValidSuggestions(
      suggestions,
      visibleDefects
    );
    setVisibleSuggestions(sortedSuggestions);
  }, [visibleDefects, suggestions]);

  useEffect(() => {
    // Find suggestion from value
    const suggestion = suggestions.find(({ defect }) => defect === value);
    if (suggestion) {
      // If found find if suggestion is in visible defects
      const visibleDefect = visibleDefects.find(
        ({ value }) => value === suggestion.defect
      );
      if (visibleDefect) {
        suggestion.name = visibleDefect.label ?? "";
        if (onSelectedDefect) {
          onSelectedDefect({ ...suggestion, name: suggestion.name ?? "" });
        }
        return;
      }
    }
    if (onSelectedDefect) {
      onSelectedDefect(undefined);
    }
  }, [value, suggestions, visibleDefects]);

  const loading = (isLoading || datasetsImageLoading) ?? false;

  return (
    <>
      {
        // FEATURE FLAG
        isDefectSuggestionsActivated === true && (
          <div className="defects-suggestion" data-testid="DefectSuggestions">
            <Flex align="flex-start" justify="space-between">
              <Flex
                wrap
                align="center"
                justify="flex-start"
                className="suggestions"
                data-testid="suggestionsList"
              >
                <span className="bulb-outlined-wrapper">
                  <BulbOutlined />
                </span>
                {loading ? (
                  // LOADING
                  <Spin
                    className="spin suggestionSpinner"
                    data-testid="suggestionSpinner"
                  />
                ) : // NOT LOADING
                visibleSuggestions.length > 0 ? (
                  visibleSuggestions.map((suggestion) => {
                    return (
                      <DefectsSuggestionItem
                        key={suggestion.name}
                        slug={suggestion.defect}
                        name={suggestion.name}
                        confidence={suggestion.confidence}
                        onChange={onChange}
                      />
                    );
                  })
                ) : (
                  <Text className="defect-suggestion-label">
                    {errorMessage}
                  </Text>
                )}
              </Flex>

              <DecisionToolButton
                value={value}
                visibleDefects={visibleSuggestions.map((suggestion) => ({
                  value: suggestion.defect ?? "",
                  label: suggestion.name ?? "",
                  confidence: suggestion.confidence,
                  suggested: suggestion.suggested,
                }))}
                datasetsImage={datasetsImageStable}
                disabled={visibleSuggestions.length === 0}
                isLoading={loading}
              />
            </Flex>
          </div>
        )
      }
    </>
  );
};

export default DefectSuggestion;
